import useServerPagination from '../../components/ui/useServerPagination';
import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { Link } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import PeopleExcelImport from '../Components/PeopleExcelImport';

export default function VendorsList() {
  const [vendors, setVendors] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user, branchId } = useAuth();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    vendor_type_id: "",
    gst: "",
    pan: "",
    status: "Active",
    contacts: [{ name: "", phone: "", email: "", is_primary: true }],
    branches: [],
    notes: "",
  });

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      password: "",
      vendor_type_id: "",
      gst: "",
      pan: "",
      status: "Active",
      contacts: [{ name: "", phone: "", email: "", is_primary: true }],
      branches: [],
      notes: "",
    });
  };

  useEffect(() => {
    if (user?.role === "staff" && branchId) {
      setForm((prev) => ({
        ...prev,
        branches: [branchId],
      }));
    }
  }, [user, branchId]);

  const server=useServerPagination({endpoint:'/vendors',onData:setVendors});
  const fetchVendors = async () => {server.reload();};

  useEffect(() => {
    const fetchData = async () => {
      const tData = await fetch("/api/vendor-types", {
        credentials: "include",
      }).then((r) => r.json());

      const types = tData.data || [];
      setVendorTypes(types);

      // ✅ FIX: auto select vendor type
      setForm((prev) => ({
        ...prev,
        vendor_type_id: prev.vendor_type_id || types[0]?.id || "",
      }));

      const bData = await fetch("/api/branches", {
        credentials: "include",
      }).then((r) => r.json());

      setBranches(bData.data || []);
    };

    fetchData();
  }, []);

  const addContact = () => {
    setForm({
      ...form,
      contacts: [
        ...form.contacts,
        { name: "", phone: "", email: "", is_primary: false },
      ],
    });
  };

  const saveVendor = async () => {
    if (!form.name || !form.email) return;

    if (!editing && !form.password) {
      alert("Password required");
      return;
    }

    const url = editing
      ? `/api/vendors/${editing}`
      : "/api/vendors";

    const method = editing ? "PUT" : "POST";

    // ✅ CLEAN PAYLOAD (MAIN FIX)
    const payload = {
      name: form.name,
      email: form.email,
      vendor_type_id: Number(form.vendor_type_id),
      gst: form.gst || "",
      pan: form.pan || "",
      status: form.status,
      notes: form.notes || "",
      branches:
        user?.role === "staff"
          ? [branchId]
          : form.branches.map((b) => Number(b)),
      contacts: form.contacts.map((c) => ({
        name: c.name,
        phone: c.phone,
        email: c.email,
        is_primary: c.is_primary,
      })),
    };

    // ✅ ONLY SEND PASSWORD IF EXISTS
    if (form.password) {
      payload.password = form.password;
    }

    console.log("FINAL PAYLOAD:", payload);

    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.status !== "success") {
      alert(data.message || "Error");
      return;
    }

    await fetchVendors();

    setShowModal(false);
    setEditing(null);
    resetForm();
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;

    await fetch(`/api/vendors/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    setVendors((prev) => prev.filter((v) => v.id !== id));
  };

  const toggleStatus = async (v) => {
    const newStatus = v.status === "Active" ? "Inactive" : "Active";

    await fetch(
      `/api/vendors/${v.id}/status?status=${newStatus}`,
      {
        method: "PATCH",
        credentials: "include",
      },
    );

    setVendors((prev) =>
      prev.map((x) => (x.id === v.id ? { ...x, status: newStatus } : x)),
    );
  };

  const openEdit = (vendor) => {
    setForm({
      name: vendor.name,
      email: vendor.email,
      password: "",
      vendor_type_id: Number(vendor.vendor_type_id) || "",
      gst: vendor.gst || "",
      pan: vendor.pan || "",
      status: vendor.status || "Active",
      notes: vendor.notes || "",
      branches: vendor.branches.map((b) => b.id),
      contacts:
        vendor.contacts?.map((c) => ({
          name: c.name,
          phone: c.phone,
          email: c.email,
          is_primary: c.primary,
        })) || [],
    });

    setEditing(vendor.id);
    setShowModal(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-serif">Vendors</h1>
          <div className="flex flex-wrap items-center gap-3">
            <PeopleExcelImport kind="vendors" onImported={fetchVendors} />
            <PermissionButton permissionModule="vendors"
              onClick={() => setShowModal(true)}
              className="px-5 py-2 rounded-xl bg-amber-400 text-black font-medium"
            >
              + Add Vendor
            </PermissionButton>
            <Link
              to="/admindashboard/vendors/vendortypes"
              className="px-5 py-2 rounded-xl bg-amber-400 ml-4 text-black font-medium"
            >
              + Add Vendor Types
            </Link>
          </div>
        </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <PaginatedTable pagination={server.pagination} className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-6 py-4 text-left">Vendor</th>
                  <th className="px-6 py-4">Type</th>
                  <th className="px-6 py-4">Primary Contact</th>
                  <th className="px-6 py-4">Branches</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!vendors.length&&<tr><td colSpan="6" className="p-10 text-center text-gray-400">{server.pagination.loading?'Loading vendors…':'No vendors match this list.'}</td></tr>}
                {vendors.map((v) => {
                  const primary = v.contacts?.find((c) => c.primary);
                  return (
                    <tr
                      key={v.id}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="px-6 py-4 font-medium">
                        <Link
                          to={`/admindashboard/vendors/${v.id}`}
                          className="text-amber-400 hover:underline"
                        >
                          {v.name || "-"}
                        </Link>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span
                          className="px-3 py-1 rounded-full text-xs"
                          style={{
                            background: `${v.type.color}22`,
                            color: v.type.color,
                          }}
                        >
                          {v.type.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {primary?.name || "-"}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {v.branches.map((b) => b.name).join(", ")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-xs ${
                            v.status === "Active"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {v.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center flex gap-3 justify-center"><TableActions>
                        {/* EDIT */}
                        <PermissionButton permissionModule="vendors" permissionAction="edit"
                          onClick={() => openEdit(v)}
                          className="text-amber-400 text-sm"
                        >
                          Edit
                        </PermissionButton>

                        {/* DISABLE */}
                        <PermissionButton permissionModule="vendors"
                          onClick={() => toggleStatus(v)}
                          className="text-yellow-400 text-sm"
                        >
                          {v.status === "Active" ? "Disable" : "Enable"}
                        </PermissionButton>

                        {/* DELETE */}
                        <PermissionButton permissionModule="vendors" permissionAction="delete"
                          onClick={() => deleteVendor(v.id)}
                          className="text-red-400 text-sm"
                        >
                          Delete
                        </PermissionButton>
                      </TableActions></td>
                    </tr>
                  );
                })}
              </tbody>
            </PaginatedTable>
          </div>
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <h2 className="text-xl font-serif mb-6">
              {editing ? "Edit Vendor" : "Add Vendor"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <input
                placeholder="Vendor Name"
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                placeholder="Email"
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                placeholder="Password"
                type="password"
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />

              <select
                value={form.vendor_type_id || ""}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                onChange={(e) =>
                  setForm({ ...form, vendor_type_id: Number(e.target.value) })
                }
              >
                {vendorTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>

              <input
                placeholder="GST Number"
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                value={form.gst}
                onChange={(e) => setForm({ ...form, gst: e.target.value })}
              />

              <input
                placeholder="PAN"
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value })}
              />
            </div>

            <p className="text-xs text-gray-400 mb-2">Contacts</p>

            <div className="space-y-3">
              {form.contacts.map((c, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    placeholder="Name"
                    className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                    value={c.name}
                    onChange={(e) => {
                      const updated = [...form.contacts];
                      updated[i].name = e.target.value;
                      setForm({ ...form, contacts: updated });
                    }}
                  />
                  <input
                    placeholder="Phone"
                    className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                    value={c.phone}
                    onChange={(e) => {
                      const updated = [...form.contacts];
                      updated[i].phone = e.target.value;
                      setForm({ ...form, contacts: updated });
                    }}
                  />
                  <input
                    placeholder="Email"
                    className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                    value={c.email}
                    onChange={(e) => {
                      const updated = [...form.contacts];
                      updated[i].email = e.target.value;
                      setForm({ ...form, contacts: updated });
                    }}
                  />
                  <PermissionButton permissionModule="vendors" onClick={addContact}>+ Add</PermissionButton>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-400 mt-6 mb-2">Branch Coverage</p>

            {branches.length === 0 ? (
              <p className="text-gray-400 text-sm">No branches found</p>
            ) : (
              branches.map((b) => {
                const isStaff = user?.role === "staff";
                const isUserBranch = Number(branchId) === b.id;

                return (
                  <label key={b.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.branches.includes(b.id)}
                      disabled={isStaff && !isUserBranch} // 🔒 LOCK OTHERS
                      onChange={(e) => {
                        if (isStaff && !isUserBranch) return; // extra safety

                        if (e.target.checked) {
                          setForm({
                            ...form,
                            branches: [...form.branches, b.id],
                          });
                        } else {
                          setForm({
                            ...form,
                            branches: form.branches.filter((x) => x !== b.id),
                          });
                        }
                      }}
                    />

                    <span
                      className={
                        isStaff && !isUserBranch
                          ? "text-gray-500 cursor-not-allowed"
                          : ""
                      }
                    >
                      {b.name}
                    </span>
                  </label>
                );
              })
            )}

            <div className="flex justify-end gap-4 mt-8">
              <PermissionButton permissionModule="vendors"
                onClick={() => setShowModal(false)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </PermissionButton>
              <PermissionButton permissionModule="vendors"
                onClick={saveVendor}
                disabled={!form.name || !form.email}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
              >
                Save Vendor
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <PermissionButton permissionModule="vendors"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>
        {children}
      </div>
    </div>
  );
}
